/**
 * Bulk Add to Cart - Table View Selection
 * Allows users to select multiple products in table view and add them all to cart at once
 */

class BulkAddToCart {
  constructor() {
    this.selectedProducts = new Map(); // Use Map instead of Set for proper tracking
    this.bulkActionsBar = document.getElementById('bulk-actions-bar');
    this.selectAllCheckbox = document.getElementById('select-all-products');
    this.productCheckboxes = [];
    this.selectedCountElement = document.getElementById('selected-count-number');
    this.bulkAddButton = document.getElementById('btn-bulk-add-to-cart');
    this.clearButton = document.getElementById('btn-clear-selection');
    this.isProcessing = false;

    this.init();
  }

  init() {
    if (!this.bulkActionsBar) {
      return;
    }

    // Initialize event listeners
    this.setupEventListeners();

    // Watch for view changes
    document.addEventListener('collectionViewChanged', (e) => {
      this.handleViewChange(e.detail.view);
    });

    // Initialize checkboxes
    this.refreshCheckboxes();
  }

  setupEventListeners() {
    // Select all checkbox
    if (this.selectAllCheckbox) {
      this.selectAllCheckbox.addEventListener('change', (e) => {
        this.toggleSelectAll(e.target.checked);
      });
    }

    // Bulk add button
    if (this.bulkAddButton) {
      this.bulkAddButton.addEventListener('click', () => {
        this.addSelectedToCart();
      });
    }

    // Clear selection button
    if (this.clearButton) {
      this.clearButton.addEventListener('click', () => {
        this.clearSelection();
      });
    }

    // Select All Visible button
    const selectAllVisibleBtn = document.getElementById('btn-select-all-visible');
    if (selectAllVisibleBtn) {
      selectAllVisibleBtn.addEventListener('click', () => {
        this.selectAllVisible();
      });
    }
  }

  selectAllVisible() {
    // Select only checkboxes that are currently visible on the page (pagination-aware)
    // This checks if the checkbox's parent product card is actually visible in the DOM
    this.productCheckboxes.forEach(checkbox => {
      const productCard = checkbox.closest('.collection-product-card');

      // Check if the product card is visible and rendered
      if (productCard && this.isElementVisible(productCard)) {
        if (!checkbox.checked) {
          checkbox.checked = true;
          // Trigger change event
          const event = new Event('change', { bubbles: true });
          checkbox.dispatchEvent(event);
        }
      }
    });
  }

  // Helper to check if element is actually visible (not display:none or hidden)
  isElementVisible(element) {
    if (!element) return false;

    // Check if element is in the DOM
    if (!document.body.contains(element)) return false;

    // Check computed styles
    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    // Check if element has dimensions
    const rect = element.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) {
      return false;
    }

    return true;
  }

  refreshCheckboxes() {
    // Get all product checkboxes
    this.productCheckboxes = Array.from(
      document.querySelectorAll('.product-select-checkbox:not([disabled])')
    );

    // Add event listeners to each checkbox
    this.productCheckboxes.forEach(checkbox => {
      // Remove existing listener if any
      checkbox.removeEventListener('change', this.handleCheckboxChange);

      // Add new listener
      checkbox.addEventListener('change', (e) => {
        this.handleCheckboxChange(e);
      });

      // Prevent checkbox clicks from triggering product link
      checkbox.addEventListener('click', (e) => {
        e.stopPropagation();
      });
    });

    // Update UI based on current selections
    this.updateUI();
  }

  handleCheckboxChange = (e) => {
    const checkbox = e.target;
    const variantId = checkbox.dataset.variantId;
    const productId = checkbox.dataset.productId;
    const productTitle = checkbox.dataset.productTitle;

    if (checkbox.checked) {
      // Use productId as key to prevent duplicates
      this.selectedProducts.set(productId, {
        variantId,
        productId,
        productTitle
      });
    } else {
      // Remove from selected products using productId
      this.selectedProducts.delete(productId);
    }

    this.updateUI();
  }

  toggleSelectAll(checked) {
    this.productCheckboxes.forEach(checkbox => {
      checkbox.checked = checked;

      // Trigger change event to update selectedProducts
      const event = new Event('change', { bubbles: true });
      checkbox.dispatchEvent(event);
    });
  }

  updateUI() {
    const count = this.selectedProducts.size;

    // Update count display
    if (this.selectedCountElement) {
      this.selectedCountElement.textContent = count;
    }

    // Show/hide bulk actions bar
    if (this.bulkActionsBar) {
      if (count > 0) {
        this.bulkActionsBar.classList.add('active');
      } else {
        this.bulkActionsBar.classList.remove('active');
      }
    }

    // Enable/disable bulk add button
    if (this.bulkAddButton) {
      this.bulkAddButton.disabled = count === 0 || this.isProcessing;
    }

    // Update select all checkbox state
    if (this.selectAllCheckbox && this.productCheckboxes.length > 0) {
      const checkedCount = this.productCheckboxes.filter(cb => cb.checked).length;
      this.selectAllCheckbox.checked = checkedCount === this.productCheckboxes.length;
      this.selectAllCheckbox.indeterminate = checkedCount > 0 && checkedCount < this.productCheckboxes.length;
    } else if (this.selectAllCheckbox) {
      this.selectAllCheckbox.checked = false;
      this.selectAllCheckbox.indeterminate = false;
    }
  }

  async addSelectedToCart() {
    if (this.selectedProducts.size === 0 || this.isProcessing) {
      return;
    }

    this.isProcessing = true;
    this.updateButtonState('loading');

    const products = Array.from(this.selectedProducts.values());
    const items = products.map(product => ({
      id: product.variantId,
      quantity: 1
    }));

    try {
      // Use Shopify's cart update API to add multiple items at once
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: items
        })
      });

      const data = await response.json();

      if (data.status && data.status === 422) {
        throw new Error(data.description || 'Unable to add items to cart');
      }

      // Success
      this.updateButtonState('success');

      // Trigger cart update events
      if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bulk-add-to-cart',
          itemCount: products.length
        });
      }

      // Update sticky cart if available
      if (typeof window.updateStickyCart === 'function') {
        window.updateStickyCart();
      }

      // Don't auto-open cart drawer - only open when clicking cart icon

      // Clear selection after successful add
      setTimeout(() => {
        this.clearSelection();
        this.updateButtonState('default');
        this.isProcessing = false;
      }, 1500);

    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.updateButtonState('error');
      alert(error.message || 'Error adding items to cart. Please try again.');

      setTimeout(() => {
        this.updateButtonState('default');
        this.isProcessing = false;
      }, 2000);
    }
  }

  updateButtonState(state) {
    if (!this.bulkAddButton) return;

    const buttonText = this.bulkAddButton.querySelector('svg').nextSibling || this.bulkAddButton.lastChild;

    switch (state) {
      case 'loading':
        this.bulkAddButton.disabled = true;
        buttonText.textContent = ' Adding to Cart...';
        this.bulkAddButton.style.opacity = '0.7';
        break;

      case 'success':
        this.bulkAddButton.disabled = true;
        buttonText.textContent = ' Added Successfully!';
        this.bulkAddButton.style.opacity = '1';
        break;

      case 'error':
        this.bulkAddButton.disabled = false;
        buttonText.textContent = ' Failed - Try Again';
        this.bulkAddButton.style.opacity = '1';
        break;

      case 'default':
      default:
        this.bulkAddButton.disabled = this.selectedProducts.size === 0;
        buttonText.textContent = ' Add Selected to Cart';
        this.bulkAddButton.style.opacity = '1';
        break;
    }
  }

  clearSelection() {
    // Uncheck all checkboxes
    this.productCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    // Clear selected products
    this.selectedProducts.clear();

    // Uncheck select all
    if (this.selectAllCheckbox) {
      this.selectAllCheckbox.checked = false;
      this.selectAllCheckbox.indeterminate = false;
    }

    // Update UI
    this.updateUI();
  }

  handleViewChange(view) {
    if (view !== 'table') {
      // Clear selection when switching away from table view
      this.clearSelection();
    } else {
      // Refresh checkboxes when switching to table view
      setTimeout(() => {
        this.refreshCheckboxes();
      }, 200);
    }
  }

  // Public method to refresh after dynamic content loading
  refresh() {
    this.refreshCheckboxes();
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  window.bulkAddToCart = new BulkAddToCart();
});

// Handle dynamic content loading (infinite scroll, AJAX, etc.)
document.addEventListener('collectionProductsLoaded', () => {
  if (window.bulkAddToCart) {
    window.bulkAddToCart.refresh();
  }
});

// Handle Shopify section reloads
document.addEventListener('shopify:section:load', () => {
  setTimeout(() => {
    if (window.bulkAddToCart) {
      window.bulkAddToCart.refresh();
    }
  }, 100);
});
